const AccountManagerService = {
  /**
   * Get permissions
   * @return {Object}
   */
  getPermissions: async () => {
    const { data } = await ovpFetch('/permissions');
    return data;
  },
  /**
   * Add user to account
   * @param {Object} user
   */
  addUser: async (user) => {
    const { data } = await ovpFetch('/users', {
      method: 'POST',
      body: JSON.stringify(user),
    });
    return data;
  },
  removeUser: async (user) => {
    await ovpFetch(`/users/${user}`, {
      method: 'DELETE',
    });
    return true;
  },
  updateAccountInformation: async (payload) => {
    await ovpFetch('/account-manager/information', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return true;
  },
  /**
   * Get users linked to account
   * @return {Object}
   */
  getUsers: async () => {
    const { data } = await ovpFetch('/users');
    return data;
  },
  getCountryCurrencies: async () => {
    const { data } = await ovpFetch('/account-manager/verification/spec');
    return data;
  },

  getBankAccounts: async () => {
    const { data } = await ovpFetch('/account-manager/transfers/bankaccount');
    return data;
  },

  getSwishBankAccounts: async () => {
    const { data } = await ovpFetch('/bankaccounts');
    return data;
  },

  addSwishBankAccount: async (payload) => {
    const { data } = await ovpFetch('/bankaccounts', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return data;
  },

  addBankAccount: async (payload) => {
    const { data } = await ovpFetch('/account-manager/transfers/bankaccount', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return data;
  },

  removeBankAccount: async (id) => {
    const { data } = await ovpFetch(`/account-manager/transfers/bankaccount/${id}`, {
      method: 'DELETE',
      iLikeItRaw: true,
    });
    return data;
  },

  removeSwishBankAccount: async (id) => {
    const { data } = await ovpFetch(`/bankaccounts/${id}`, {
      method: 'DELETE',
      iLikeItRaw: true,
    });
    return data;
  },

  setDefaultBankAccount: async (payload) => {
    const { data } = await ovpFetch('/account-manager/transfers/bankaccount', {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
    return data;
  },

  getAccount: async (id) => {
    const { data } = await ovpFetch(`/accounts/${id}`);
    return data;
  },

  /**
   * Check availability of accountname
   * @param {String} name
   * @returns {Object}
   */
  checkAccountName: async (name) => {
    const { data } = await ovpFetch('/accounts/checkname', {
      method: 'POST',
      body: JSON.stringify({
        name,
      }),
      headers: {
        Authorization: 'token dxLJiHtMd7knwQfkMQxz6DkraY1eDIo1v67qxKmX',
      },
    });
    return data;
  },

  updateAccount: async (id, payload) => {
    const { data } = await ovpFetch(`/accounts/${id}`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return data;
  },

  getAccountSettings: async () => {
    const { data } = await ovpFetch('/v2/settings');
    return data;
  },

  getAccountOnboarding: async () => {
    const { data } = await ovpFetch('/settings/onboarding');
    return data;
  },

  unassumeAccount: async () => {
    await ovpFetch('/authentication/assume', { method: 'DELETE', iLikeItRaw: true });
    return true;
  },

  assumeAccount: async (id, permission) => {
    await ovpFetch('/authentication/assume', {
      method: 'POST',
      body: JSON.stringify({
        account_id: id,
        permission,
      }),
    });
    return true;
  },

  setLegalCountry: async (country) => {
    await ovpFetch('/account-manager/verification/legalcountry', {
      method: 'POST',
      iLikeItRaw: true,
      body: JSON.stringify({ country }),
    });
    return true;
  },
  getChildAccounts: async (payload) => {
    const params = Object.keys(payload).map(key => `${encodeURIComponent(key)}=${encodeURIComponent(payload[key])}`).join('&');

    const { data, headers } = await ovpFetch(`/accounts?${params}`);
    const pagination = {
      max_page: 1,
      current_page: payload.page,
    };
    let total = 0;

    const { link } = headers;
    if (link) {
      const linkSplit = link.split(',');
      const lastIndex = linkSplit.findIndex(rel => rel.includes('rel="last"'));
      const prevIndex = linkSplit.findIndex(rel => rel.includes('rel="prev"'));
      if (lastIndex > -1) {
        pagination.max_page = parseInt(linkSplit[lastIndex].match(/(?:&page=|\?page=)(\d+)/)[1], 10);
      } else if (prevIndex > -1) {
        pagination.max_page = parseInt(linkSplit[prevIndex].match(/(?:&page=|\?page=)(\d+)/)[1], 10) + 1;
      }
      pagination.current_page = pagination.max_page < pagination.current_page ? pagination.max_page : pagination.current_page;
    }

    if (headers['total-count']) {
      total = headers['total-count'];
    }

    return {
      accounts: data,
      pagination,
      total,
    };
  },
  createChildAccount: async (payload) => {
    await ovpFetch('/accounts', {
      method: 'POST',
      iLikeItRaw: true,
      body: JSON.stringify(payload),
    });
    return true;
  },
  verifyInvite: async (uuid) => {
    const { data } = await ovpFetch(`/account-manager/invite/${uuid}`, {
      headers: {
        Authorization: 'token dxLJiHtMd7knwQfkMQxz6DkraY1eDIo1v67qxKmX',
      },
    });
    return data;
  },
  declineInvite: async (uuid) => {
    await ovpFetch(`/account-manager/invite/${uuid}`, {
      method: 'DELETE',
      iLikeItRaw: true,
      headers: {
        Authorization: 'token dxLJiHtMd7knwQfkMQxz6DkraY1eDIo1v67qxKmX',
      },
    });
    return true;
  },
  onboardingVerification: async () => {
    const { data } = await ovpFetch('/account-manager/stripe/onboarding/verification');
    return data;
  },
  onboardingUpdate: async () => {
    const { data } = await ovpFetch('/account-manager/stripe/onboarding/update');
    return data;
  },
  getConnectLink: async () => {
    const { data } = await ovpFetch('/account-manager/stripe/connect/link');
    return data;
  },
  connectStripeAccount: async (code) => {
    await ovpFetch('/account-manager/stripe/connect/account', {
      method: 'POST',
      body: JSON.stringify({
        code,
      }),
    });
    return true;
  },
  shoppingSettings: async () => {
    const { data } = await ovpFetch('/v2/settings/shopping');
    return data;
  },
  deleteShoppingSettings: async () => {
    const { data } = await ovpFetch('/v2/settings/shopping', {
      method: 'DELETE',
      iLikeItRaw: true,
    });
    return data;
  },
  updateSettings: async (payload) => {
    await ovpFetch('/v2/settings', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
};

// eslint-disable-next-line
export { AccountManagerService };
